/**
 * @file
 *
 * This file aims to provide an API that CTI code can use to check for
 * cookie/localStorage/sessionStorage consent, without the CTI code needing
 * to know if a project is using the EU Cookie Compliance module, or CookieBot,
 * or Civic Cookie Control, etc etc.
 *
 * The idea is we update this API over time to provide more consent solutions
 * and more methods.
 */

// Make a 'Drupal.Voyager' object if one doesn't exist already.
window.Drupal.Voyager = window.Drupal.Voyager || {};

window.Drupal.Voyager.storageConsentApi = {
  isDebug() {
    return window.location.href.includes('.ctidev');
  },
  debugLog(message, level = 'log') {
    if (this.isDebug()) {
      // eslint-disable-next-line no-console
      console[level](`🍪: ${message}

(This debug message is only shown on URLs that contain '.ctidev')`);
    }
  },
  getActiveProvider() {
    let activeProvider;

    // Loop through our list of providers and run each providers 'test' function.
    // The first test function that passes is used to determine the active
    // provider.
    _.forOwn(this.providers, (provider, key) => {
      if (provider.test()) {
        activeProvider = key;
      }
    });

    return activeProvider;
  },
  hasAgreed(category) {
    const provider = this.getActiveProvider();

    if (typeof provider === 'undefined') {
      this.debugLog(
        `
Returning FALSE (failing safe) as we have no storage consent provider to check against.

This could be because the call to the storage consent API has run before the third-party provider code has run (as third-party JS often runs later, and even the Drupal EU Cookie Compliance module sets its JS to use deferred loading.)

To get around this, you could check for consent *later* - perhaps by checking for consent when setting storage rather than reading it, or experiment with Drupal library dependencies to influence the order that your code runs in. A final option would be to create a setTimeout loop that tries to run your code until a provider is found.

If this site uses no cookie/storage consent provider *intentionally*, then uncomment the dummy 'intentionallyNoProviderInUse' provider within the providers object.`,
        'warn',
      );

      return false;
    }

    // Run the 'hasAgreed' function from the active provider's object.
    const consent = this.providers[this.getActiveProvider()].hasAgreed(
      category,
    );

    if (consent) {
      this.debugLog(
        `✅: Consent granted for '${category}' storage via ${provider}`,
      );
    } else {
      this.debugLog(
        `❌: Consent denied for '${category}' storage via ${provider}.

User may have not reacted to cookie consent prompt, or they may have declined cookies.`,
      );
    }

    return consent;
  },
  providers: {
    // If this site really uses no cookie/storage consent provider then you
    // can uncomment the dummy provider below.
    // intentionallyNoProviderInUse: {
    //   test() {
    //     return true;
    //   },
    //   hasAgreed() {
    //     return true;
    //   },
    // },
    euCookieComplianceDrupalModuleNoCategories: {
      test() {
        return (
          typeof Drupal.eu_cookie_compliance !== 'undefined' &&
          window.drupalSettings.eu_cookie_compliance.cookie_categories
            .length === 0
        );
      },
      hasAgreed() {
        const status = Drupal.eu_cookie_compliance.getCurrentStatus();

        // See www/modules/contrib/eu_cookie_compliance/js/eu_cookie_compliance.js
        // for what each number means.
        switch (status) {
          case 1:
            return true;
          case 2:
            return true;
          default:
            return false;
        }
      },
    },
    euCookieComplianceDrupalModuleWithCategories: {
      test() {
        return (
          typeof window.Drupal.eu_cookie_compliance !== 'undefined' &&
          window.drupalSettings.eu_cookie_compliance.cookie_categories.length >
            0
        );
      },
      hasAgreed(category) {
        const categories =
          window.drupalSettings.eu_cookie_compliance.cookie_categories;

        if (!categories.includes(category)) {
          window.Drupal.Voyager.storageConsentApi.debugLog(
            `${category} was not found in this site's list of EU Cookie Compliance module cookie categories`,
            'warn',
          );
        }

        return Drupal.eu_cookie_compliance.hasAgreedWithCategory(category);
      },
    },
    civic: {
      test() {
        return (
          typeof window.CookieControl !== 'undefined' &&
          typeof window.CookieControl.info !== 'undefined' &&
          window.CookieControl.info().includes('Cookie Control')
        );
      },
      hasAgreed(category) {
        const categories = window.CookieControl.config().optionalCookies;
        const index = _.findIndex(categories, { name: category });

        if (index !== -1) {
          return window.CookieControl.getCategoryConsent(index);
        }

        window.Drupal.Voyager.storageConsentApi.debugLog(
          `Couldn't find the category ${category} in the list of Civic optionalCookies`,
          'error',
        );
        return false;
      },
    },
    cookiebot: {
      test() {
        return typeof window.Cookiebot !== 'undefined';
      },
      hasAgreed(category) {
        const consents = window.Cookiebot.consent;

        if (consents[category]) {
          return consents[category];
        }

        window.Drupal.Voyager.storageConsentApi.debugLog(
          `Couldn't find the category ${category} in the list of Cookiebot consents`,
          'error',
        );

        return false;
      },
    },
    oneTrust: {
      test() {
        return (
          typeof window.OneTrust !== 'undefined' &&
          typeof window.OnetrustActiveGroups !== 'undefined'
        );
      },
      hasAgreed(category) {
        const consents = window.OnetrustActiveGroups.split(',');
        const oneTrustCategories = {
          necessary: 'C0001', // Strictly Necessary Cookies
          performance: 'C0002', // Performance (analytics) Cookies
          preferences: 'C0003', // Functional Cookies
          targeting: 'C0004', // Targeting Cookies
          social: 'C0005', // Social Media Cookies
        };

        if (
          oneTrustCategories[category] !== 'undefined' &&
          consents.indexOf(oneTrustCategories[category]) !== -1
        ) {
          return true;
        }

        window.Drupal.Voyager.storageConsentApi.debugLog(
          `Couldn't find the category '${category}' in the list of OneTrust consents`,
          'error',
        );

        return false;
      },
    },
  },
};
